import Skeleton from '@mui/material/Skeleton';
import type { Table } from '@tanstack/react-table';
import type React from 'react';
import { getListViewColumnStyle, getListViewFillColumnId } from './ListViewColumnSizing';
import type { DensityConfig, ListViewRow } from './listView.types';

interface ListViewSkeletonProps {
  table: Table<ListViewRow>;
  density: DensityConfig;
  classes: Record<string, string>;
}

const SKELETON_ROW_COUNT = 8;

const ListViewSkeleton: React.FC<ListViewSkeletonProps> = ({ table, density, classes }) => {
  const visibleColumns = table.getVisibleLeafColumns();
  const visibleColumnIds = visibleColumns.map((c) => c.id);
  const fillColumnId = getListViewFillColumnId(visibleColumnIds);

  let dynamicIndex = 0;

  return (
    <>
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => {
        const isOdd = rowIndex % 2 === 1;
        const rowClassName = [classes.bodyRow, isOdd ? classes.bodyRowOdd : ''].filter(Boolean).join(' ');
        dynamicIndex = 0;

        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders have no stable id
          <tr key={`skeleton-${rowIndex}`} className={rowClassName}>
            {visibleColumns.map((column) => {
              const style = getListViewColumnStyle(column.id, column.getSize(), fillColumnId);
              const id = column.id;

              if (id === 'select') {
                return (
                  <td key={id} className={`${classes.bodyCell} ${classes.selectCell}`} style={style}>
                    <Skeleton variant="circular" width={20} height={20} animation="wave" sx={{ mx: 'auto' }} />
                  </td>
                );
              }
              if (id === 'type') {
                return (
                  <td key={id} className={`${classes.bodyCell} ${classes.typeCell}`} style={style}>
                    <Skeleton
                      variant="rounded"
                      width={24}
                      height={18}
                      animation="wave"
                      sx={{ mx: 'auto', borderRadius: 3 }}
                    />
                  </td>
                );
              }
              if (id === 'thumbnail') {
                return (
                  <td key={id} className={`${classes.bodyCell} ${classes.thumbnailCell}`} style={style}>
                    <Skeleton
                      variant="rectangular"
                      width={density.thumbWidth}
                      height={density.thumbHeight}
                      animation="wave"
                      sx={{ borderRadius: 0.5 }}
                    />
                  </td>
                );
              }
              if (id === 'actions') {
                return (
                  <td key={id} className={`${classes.bodyCell} ${classes.actionsCell}`} style={style}>
                    <Skeleton variant="circular" width={20} height={20} animation="wave" sx={{ mx: 'auto' }} />
                  </td>
                );
              }

              // Dynamic column — first is wider (name/title), rest are narrower
              const isFirst = dynamicIndex === 0;
              dynamicIndex++;
              return (
                <td key={id} className={classes.bodyCell} style={style}>
                  <Skeleton
                    variant="text"
                    width={isFirst ? '70%' : '50%'}
                    animation="wave"
                    sx={{ fontSize: density.fontSize }}
                  />
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );
};

export default ListViewSkeleton;
